import Debug from 'debug';
import { ok, err, Result } from '@xlearn/common'
import { shuffleArray } from '@/app/shared/utils';
import { CancelToken } from '@/app/shared/command-queue';
import { Question } from '@/app/question';
import { LoadQuestionsJob, LoadQuestionsError } from '@/app/question/store';
import { QuestionReference } from '@/app/quiz';
import { PracticeSessionService } from '@/app/practice-session/service';
import { PracticeStateMachineAbstractState, PracticeStartOptions, PracticeStateMachineCommonEvents } from './state'
import { LoadPracticeJob, LoadPracticeError } from './load-practice-job';
import { PracticeStateMachineData, PracticeQuestion } from '../data'
import { Adaptivity } from './adaptivity'

const debug = Debug('practice-starting-state')

interface Events extends PracticeStateMachineCommonEvents {
  onError: (err: unknown) => void
  onStarted: () => void
}

type CommonErrorCode = LoadPracticeError | LoadQuestionsError
type StartErrorCode = CommonErrorCode
type ContinueErrorCode = CommonErrorCode | 'continue-question-not-found'

export class PracticeStartingState extends PracticeStateMachineAbstractState<Events> {

  public readonly name = 'starting'

  constructor(
    private startOptions: PracticeStartOptions,
    private loadPracticeJob: LoadPracticeJob,
    private loadQuestionsJob: LoadQuestionsJob,
    private adaptivity: Adaptivity,
    private data: PracticeStateMachineData,
    private practiceSessionService: PracticeSessionService) {
    super()
  }

  enter() {
    debug('enter')
    const startJob = this.execute(this.cancelToken)
    startJob.catch(err => {
      debug('startJob error', err)
      this.triggerEvent(events => events.onError(err))
    })
  }

  exit() {
    debug('exit')
  }

  cancel() {
    debug('beginCancel')
    this.beginCancel()
  }

  private async execute(cancelToken: CancelToken) {
    let result: Result<true, StartErrorCode | ContinueErrorCode>
    switch (this.startOptions.type) {
      case 'new':
        result = await this.startNew(cancelToken)
        break
      case 'continue':
        result = await this.continueExisting(cancelToken)
        break
    }

    const isCanceled = cancelToken.cancel ||
          (!result.success && result.error === 'canceled')

    if (isCanceled) {
      debug('endCancel')
      this.endCancel()
      return
    }

    if (!result.success) {
      const error = result.error
      this.triggerEvent(events => events.onError(error))
      return
    }

    this.triggerEvent(events => events.onStarted())
  }

  private async startNew(cancel: CancelToken): Promise<Result<true, StartErrorCode>> {

    if (this.startOptions.type !== 'new') { throw new Error('Coding error') }
    const { sessionId, practiceId, aspect } = this.startOptions
    let { questionCount } = this.startOptions
    
    const practiceResult = await this.loadPractice(practiceId, cancel)
    if (!practiceResult.success) {
      return practiceResult
    }
    const practice = practiceResult.value

    const questionReferences = practice.questions
    questionCount = this.validateQuestionCount(questionCount, practice.questionCount)

    const practiceQuestionsResult = await this.resolveQuestions(questionReferences, questionCount, cancel)
    if (!practiceQuestionsResult.success) {
      return practiceQuestionsResult
    }

    const { questions, level } = practiceQuestionsResult.value
    const adaptivityState = {
      references: questionReferences,
      level: level
    }

    this.practiceSessionService.startSession(sessionId, practiceId, aspect, {
      levelBefore: level,
      questionIds: questions.map(x => x.id),
      questionIndex: 0
    })

    debug('level before starting the practice', level)

    this.data.started({ sessionId, questions, adaptivity: adaptivityState })

    return ok(true)
  }

  private async continueExisting(cancel: CancelToken): Promise<Result<true, ContinueErrorCode>> {
    if (this.startOptions.type !== 'continue') { throw new Error('Coding error') }
    
    const { sessionId, practiceId, state } = this.startOptions
    debug('continuing practice session', sessionId, practiceId)

    const practiceResult = await this.loadPractice(practiceId, cancel)
    if (!practiceResult.success) {
      return practiceResult
    }
    const practice = practiceResult.value

    const questionIds = state.questionIds
    const questionReferencesResult = this.filterReferencesByQuestionIds(practice.questions, questionIds)
    if (!questionReferencesResult.success) {
      return questionReferencesResult
    }

    const questionReferences = questionReferencesResult.value

    const questionsResult = await this.loadQuestions(questionReferences, cancel)
    if (!questionsResult.success) {
      return questionsResult
    }
    const questions = questionsResult.value

    this.data.continued({ sessionId, questions, adaptivity: {
      references: practice.questions,
      level: state.levelBefore
    } }, {
      questionStates: state.questionStates,
      navigation: state.navigation,
      questionTimes: state.questionTimes

    })

    debug('practice continuation finished', questions)
    return ok(true)
  }

  private async resolveQuestions(questionReferences: QuestionReference[], questionCount: number,
                                 cancel: CancelToken) {

    const calculationResult = this.calculateQuestionIds(questionReferences, questionCount)
    const usedQuestionReferences = this.filterReferencesByQuestionIds(
      questionReferences, calculationResult.questionIds)

    if (!usedQuestionReferences.success) {
      throw new Error(`Coding error: missing on of the questions: ${JSON.stringify(calculationResult.questionIds)}`)
    }
    const practiceQuestions = await this.loadQuestions(usedQuestionReferences.value, cancel)
    if (!practiceQuestions.success) {
      return practiceQuestions
    }

    return ok({
      questions: practiceQuestions.value,
      level: calculationResult.level
    })
  }

  private filterReferencesByQuestionIds(questionReferences: QuestionReference[], questionIds: string[]) {
    const filtered = questionIds.map(id => {
      return questionReferences.find(x => x.questionId == id)
    })

    for (let item of filtered) {
      if (!item) {
        return err('continue-question-not-found' as 'continue-question-not-found')
      }
    }

    return ok(filtered as QuestionReference[])
  }

  private async loadQuestions(references: QuestionReference[], cancelToken: CancelToken) {
    debug('waiting for questions to load', references)
    const questionResult = await this.loadQuestionsJob.execute(references.map(q => q.questionId), cancelToken)
    if (!questionResult.success) {
      return questionResult
    }

    const practiceQuestions = references.map((ref, i) => {
      return this.mapPracticeQuestion(ref, questionResult.value[i])
    })

    return ok(practiceQuestions)
  }

  private mapPracticeQuestion(ref: QuestionReference, question: Question): PracticeQuestion {

    const practiceQuestion: PracticeQuestion = {
      id: ref.questionId,
      type: question.type,
      minScore: ref.minScore,
      maxScore: ref.maxScore,
      question: question.question,
      feedback: question.feedback,
      content: question.content,
      contentScore: question.contentScore
    }
    return practiceQuestion    
  }

  private calculateQuestionIds(questionReferences: QuestionReference[], questionCount: number) {
    const calculationResult = this.adaptivity.calculate(questionReferences, questionCount)
    if (calculationResult !== false) {
      return calculationResult
    }

    debug('adaptive question calculation failed, shuffling')
    const shuffledQuestionIds = shuffleArray(questionReferences, questionCount).map(x => x.questionId)
    return {
      questionIds: shuffledQuestionIds,
      level: 0
    }
  }

  private validateQuestionCount(questionCount: number, practiceQuestionCount: number) {
    if (questionCount > practiceQuestionCount) { return practiceQuestionCount }
    if (questionCount < 1) { return 1 }

    return questionCount
  }

  private async loadPractice(practiceId: string, cancelToken: CancelToken) {
    debug('waiting for practice to load', practiceId)
    const practiceResult = await this.loadPracticeJob.execute(practiceId, cancelToken)
    return practiceResult
  }
}
